import React from 'react';
import { cn } from '../../../lib/utils';

const UNAVAILABLE_TEXT = 'Data Not Available';

interface DataMatrixTableData {
    headers: string[];
    rows: { title: string; values: (string | number | null)[] }[];
    transpose?: boolean;
}

interface DataMatrixTableProps {
    data: DataMatrixTableData;
}

const DataMatrixTable: React.FC<DataMatrixTableProps> = ({ data }) => {
    if (!data || !data.headers || data.headers.length === 0 || data.rows.length === 0) {
        return <div className="text-center py-8 text-gray-500">No data available for this section.</div>;
    }

    // restructure the data for transposed layout
    if (data.transpose) {
        return (
            <div className="border border-[#E5E5E5] rounded-xl shadow-sm overflow-x-auto">
                <table className="min-w-full">
                    <thead className="bg-slate-100">
                        <tr>
                            {/* Create headers from row titles */}
                            {data.rows.map((row, index) => (
                                <th key={index} scope="col" className="px-2 py-3 text-left text-sm font-medium text-[#737373] whitespace-nowrap">
                                    {row.title}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#E5E5E5]">
                        {/* Each record becomes a row, each row.title becomes a column */}
                        {data.headers.slice(1).map((header, headerIndex) => ( // Skip first header as it's usually "Field"
                            <tr key={headerIndex} className={cn(headerIndex % 2 !== 0 ? "bg-white" : "bg-gray-25")}>
                                {data.rows.map((row, rowIndex) => (
                                    <td key={rowIndex} className="px-2 py-4 text-sm font-medium text-[#0A0A0A]">
                                        <span className={cn(
                                            row.values[headerIndex] === UNAVAILABLE_TEXT || 
                                            row.values[headerIndex] === null || 
                                            row.values[headerIndex] === undefined
                                                ? "text-gray-400 font-normal italic"
                                                : "text-[#0A0A0A] font-normal"
                                        )}>
                                            {row.values[headerIndex] || UNAVAILABLE_TEXT}
                                        </span>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    // Non-transposed layout
    return (
        <div className="border border-[#E5E5E5] rounded-xl shadow-sm overflow-x-auto">
            <table className="min-w-full">
                <thead className="bg-slate-100">
                    <tr>
                        {data.headers.map((header, index) => (
                            <th key={index} scope="col" className="px-2 py-3 text-left text-sm font-medium text-[#737373]">
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#E5E5E5]">
                    {data.rows.map((row, rowIndex) => (
                        <tr key={row.title} className={cn(rowIndex % 2 !== 0 ? "bg-white" : "bg-gray-25")}>
                            <td className="px-2 py-4 text-sm font-medium text-[#0A0A0A]">{row.title}</td>
                            {row.values.map((value, valueIndex) => (
                                <td key={valueIndex} className="px-2 py-4 text-sm text-[#0A0A0A] font-normal">
                                    <span className={cn(
                                        value === UNAVAILABLE_TEXT || value === null || value === undefined
                                            ? "text-gray-400 font-normal italic"
                                            : "text-[#0A0A0A] font-normal"
                                    )}>
                                        {value || UNAVAILABLE_TEXT}
                                    </span>
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default DataMatrixTable;